import {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {IOfferModel, IOfferUpdatePayload} from '../cors/_models'
import {updateOffer} from '../cors/_requests'
import SharedData from '../../common/SharedData'
import DeliveryPathChip from '../offer-list/DeliveryPathChip'
import WhyNotWinning from './WhyNotWinning'
import {displayQuantity, isShopScoped, shelfErrorOf, shopLabel, shopOf} from '../../common/stockScope'
import AllBranchesCheckbox from '../../../components/AllBranchesCheckbox'

/**
 * Edit form for one offer. Mirrors backend's normalizeAndValidate rules
 * (see SupplierOfferController) so we fail fast on the client:
 *
 *   - price > 0
 *   - quantity >= 0
 *   - supplier_sku <= 64 chars
 *
 * Two fields are read-only by business rule (server-controlled):
 *   - cost                — derived from price × supplier margin
 *   - warehouse_quantity  — set by the FC receiving process
 *
 * Read-only context (banners, NOT inputs):
 *   - admin_override_active → red banner: "your price is overridden"
 *   - effective_delivery_path / hours → info chip
 *   - is_winning_offer === false → soft "not winning" hint
 *
 * Submit returns the freshly-decorated offer; parent updates table.
 */

type IProps = {
  offer: IOfferModel
  onSaved: (updated: IOfferModel) => void
  onCancel: () => void
}

const toNum = (v: string): number => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : NaN
}

const OfferEditForm: FC<IProps> = ({offer, onSaved, onCancel}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()

  // A package is assembled from its components: its price is their price and its
  // quantity is floor(component stock / per-pack size), both recomputed whenever
  // a component moves. The offer endpoints refuse edits to either
  // (cannot_offer_on_package), so the drawer must not offer them — a save here
  // could only fail, and a number typed in would be overwritten regardless.
  const isPackage = Number(offer.is_offer) === 1

  const lockedForPackage = (field: string, control: JSX.Element) =>
    isPackage ? (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`pkg-offer-${field}`}>
            {formatMessage({id: 'package_fields_are_derived'})}
          </Tooltip>
        }
      >
        <span className="d-block" style={{cursor: 'not-allowed'}}>{control}</span>
      </OverlayTrigger>
    ) : (
      control
    )

  // Mart v2: the price and on/off for every shop branch, not only the one picked in the header bar.
  const [allBranches, setAllBranches] = useState(false)
  const [price, setPrice] = useState<string>(String(parseFloat(offer.price).toFixed(2)))
  // A partner shop's account edits its SHELF (offer.shop_quantity); the server sends the number to
  // the store's shelf and never to national supplier stock.
  const shop = shopOf(currentUser)
  const shopScoped = isShopScoped(offer)
  const [quantity, setQuantity] = useState<string>(String(displayQuantity(offer)))
  const [supplierSku, setSupplierSku] = useState<string>(offer.supplier_sku ?? '')
  const [isActive, setIsActive] = useState<boolean>(offer.is_active === 1)

  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const validate = (): string | null => {
    const p = toNum(price)
    const q = parseInt(quantity, 10)

    if (!Number.isFinite(p) || p <= 0) return formatMessage({id: 'offers.form.err.price_invalid'})
    if (!Number.isFinite(q) || q < 0) return formatMessage({id: 'offers.form.err.qty_invalid'})
    if (supplierSku.length > 64) return formatMessage({id: 'offers.form.err.sku_too_long'})
    return null
  }

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const v = validate()
    if (v) {
      setError(v)
      return
    }
    setError(null)
    setSubmitting(true)

    // Payload intentionally omits cost + warehouse_quantity. Server
    // derives cost from price × margin; warehouse_quantity is set by
    // the receiving process. The API allowlist drops these silently
    // even if a stale client sends them.
    const payload: IOfferUpdatePayload = {
      price: toNum(price),
      supplier_quantity: parseInt(quantity, 10),
      supplier_sku: supplierSku.trim() === '' ? null : supplierSku.trim(),
      is_active: isActive ? 1 : 0,
    }

    try {
      const res = await updateOffer(
        offer.product_supplier_id,
        payload,
        currentUser?.token,
        language,
        allBranches,
      )
      const updated = res.data?.data
      if (updated) onSaved(updated)
    } catch (err: any) {
      const refused = shelfErrorOf(err)
      if (refused) {
        setError(
          formatMessage(
            {id: `shop_scope.err.${refused.error}`, defaultMessage: refused.message},
            {held: refused.held},
          ),
        )
        return
      }
      const apiErr = err?.response?.data?.error
      setError(
        apiErr
          ? formatMessage(
              {id: `offers.form.api_err.${apiErr}`, defaultMessage: apiErr},
            )
          : formatMessage({id: 'something_wrong_desc'}),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const isWinning = offer.is_winning_offer === 1

  return (
    <form className="hsb-of-form" onSubmit={submit} aria-busy={submitting}>
      <div className="hsb-of-form-body">
        {/* Admin-override banner — supplier sees their price is replaced. */}
        {offer.admin_override_active ? (
          <div className="hsb-of-alert hsb-of-alert--danger" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">info</span>
            <div>
              <div className="hsb-of-alert-title">
                {formatMessage({id: 'offers.form.banner.admin_override_title'})}
              </div>
              <div className="hsb-of-alert-body">
                {formatMessage({id: 'offers.form.banner.admin_override_body'})}
              </div>
            </div>
          </div>
        ) : null}

        {/* Lost-buy-box hint — soft; only when not winning + active. */}
        {!isWinning && offer.is_active === 1 ? (
          <div className="hsb-of-alert hsb-of-alert--warning" role="status">
            <span className="material-symbols-outlined" aria-hidden="true">warning</span>
            <div>
              <div className="hsb-of-alert-title">
                {formatMessage({id: 'offers.form.banner.not_winning_title'})}
              </div>
              <div className="hsb-of-alert-body">
                {formatMessage({id: 'offers.form.banner.not_winning_body'})}
              </div>
              {/* T9: on-demand anonymized "why didn't I win" breakdown. */}
              <WhyNotWinning productSupplierId={offer.product_supplier_id} />
            </div>
          </div>
        ) : null}

        {/* Effective delivery — read-only, shown next to the rate fields. */}
        <div style={{marginBottom: 20}}>
          <label className="hsb-of-section-label">
            {formatMessage({id: 'offers.form.effective_delivery'})}
          </label>
          <div>
            <DeliveryPathChip
              path={offer.effective_delivery_path}
              hours={offer.effective_delivery_hours}
            />
          </div>
          <div className="hsb-of-help" style={{marginTop: 6}}>
            {formatMessage({id: 'offers.form.effective_delivery_help'})}
          </div>
        </div>

        <div className="hsb-of-form-grid">
          <div className="hsb-of-form-group">
            <label className="hsb-of-form-label">
              <span className="hsb-of-req">*</span>{' '}
              {formatMessage({id: 'offers.form.price'})}
            </label>
            {lockedForPackage('price',
              <input
                type="number"
                step="0.01"
                min="0.01"
                className="hsb-of-control"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={submitting}
                readOnly={isPackage}
                style={isPackage ? {pointerEvents: 'none', backgroundColor: '#f1f1f2', color: '#7e8299'} : undefined}
                required
              />
            )}
          </div>

          <div className="hsb-of-form-group">
            <label className="hsb-of-form-label">
              {shopScoped && shop
                ? formatMessage({id: 'shop_scope.shelf_at'}, {store: shopLabel(shop)})
                : formatMessage({id: shopScoped ? 'shop_scope.shelf_quantity' : 'offers.form.quantity'})}
            </label>
            {lockedForPackage('quantity',
              <input
                type="number"
                min="0"
                className="hsb-of-control"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={submitting}
                readOnly={isPackage}
                style={isPackage ? {pointerEvents: 'none', backgroundColor: '#f1f1f2', color: '#7e8299'} : undefined}
              />
            )}
            <div className="hsb-of-help">
              {formatMessage({id: shopScoped ? 'shop_scope.shelf_help' : 'offers.form.quantity_help'})}
            </div>
          </div>

          {/* In-FC — read-only display. The FC receiving process is the
              only thing that bumps this; suppliers can see the current
              count but can't change it. Not shown to a partner shop: its
              stock is its own shelf, not Dumyah's FC. */}
          {!shopScoped && (
          <div className="hsb-of-form-group hsb-of-col-2">
            <label className="hsb-of-form-label">
              {formatMessage({id: 'offers.form.warehouse_quantity'})}
            </label>
            <input
              type="text"
              readOnly
              className="hsb-of-control"
              value={String(offer.warehouse_quantity)}
            />
            <div className="hsb-of-help">
              {formatMessage({id: 'offers.form.warehouse_quantity_readonly_help'})}
            </div>
          </div>
          )}

          <div className="hsb-of-form-group hsb-of-col-2">
            <label className="hsb-of-form-label">
              {formatMessage({id: 'offers.form.supplier_sku'})}
            </label>
            <input
              type="text"
              maxLength={64}
              className="hsb-of-control"
              value={supplierSku}
              onChange={(e) => setSupplierSku(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="hsb-of-col-2">
            <label className="hsb-of-switch">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={submitting}
              />
              <span className="hsb-of-switch-track">
                <span className="hsb-of-switch-thumb" />
              </span>
              <span className="hsb-of-switch-label">
                {formatMessage({id: 'offers.form.is_active'})}
              </span>
            </label>
          </div>
        </div>

        <AllBranchesCheckbox checked={allBranches} onChange={setAllBranches} disabled={submitting} />

        {error ? (
          <div className="hsb-of-alert hsb-of-alert--danger" role="alert" style={{marginTop: 16, marginBottom: 0}}>
            <span className="material-symbols-outlined" aria-hidden="true">error</span>
            <div className="hsb-of-alert-body">{error}</div>
          </div>
        ) : null}
      </div>

      <div className="hsb-of-form-foot">
        <button
          type="button"
          className="hsb-of-btn-ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          {formatMessage({id: 'offers.form.cancel'})}
        </button>
        <button type="submit" className="hsb-of-btn-primary" disabled={submitting}>
          {submitting
            ? formatMessage({id: 'offers.form.saving'})
            : formatMessage({id: 'offers.form.save'})}
        </button>
      </div>
    </form>
  )
}

export default OfferEditForm
